import { Col, Row } from 'antd';
import { Pin } from 'types';
import { formatToDate } from 'utils/formatToDate';

import { DocumentDetailsModel } from 'api/documents/documents.model';
import { BasicCard } from 'components/BasicCard';
import CardRow from 'components/CardRow';
import CompositionRow from 'components/CompositionRow';
import Detail from 'components/Detail';
import Map from 'components/Map';
import { extractCoordinatesFromProduct } from 'components/Map/utils';
import SectionTitle from 'components/SectionTitle';
import SubTitle from 'components/SubTitle';
import useCertificateHelper from 'hooks/useCertificateHelper';
import {
  ADDITIONAL_DETAILS,
  CertificateDetailsModel,
  NESTED_DETAILS,
  PRODUCER_DETAILS,
} from 'models/certificate.model';

import RelatedCertificates from '../components/RelatedCertificates';
import Template from '../components/Template';

const BatteryRepurpose = ({
  additionalDetails = {} as CertificateDetailsModel,
  ...document
}: DocumentDetailsModel<CertificateDetailsModel>) => {
  const { getLabel, getValueByLabel } = useCertificateHelper();

  const pins: Pin[] = [];

  if (additionalDetails[ADDITIONAL_DETAILS.PRODUCER]) {
    const coordinates = extractCoordinatesFromProduct(
      additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
    );

    if (coordinates) {
      pins.push({
        coordinates,
        iconPath: '/pin-blue.svg',
      });
    }
  }

  return (
    <Template
      type={document.category}
      jobId={document.jobId}
      favourite={document.favourite}
      batchCard={
        <>
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail label={getLabel('passportId')} value={document.documentBlockchainAddress} />
          <CardRow>
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.ASSEMBLY_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.ASSEMBLY_NUMBER]}
            />
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.CONTROL_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.CONTROL_NUMBER]}
            />
          </CardRow>
        </>
      }
      detailsCard={
        <CardRow>
          <Detail
            label={getLabel(NESTED_DETAILS.EVMODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.EVMODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PRODUCERS)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PRODUCERS,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_CELL_MODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_CELL_MODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PRODUCTION_DATE)}
            value={formatToDate(
              getValueByLabel(
                NESTED_DETAILS.BATTERY_PRODUCTION_DATE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )
            )}
          />
          <Detail
            label={getLabel('baterryPackTechnicalReviewCertificateUID')}
            value={additionalDetails.productId}
          />
        </CardRow>
      }
    >
      <Row gutter={[16, 16]}>
        <Col md={24} lg={10}>
          <SectionTitle>{getLabel('repurpose')}</SectionTitle>
          <CardRow>
            <Detail
              label={getLabel(NESTED_DETAILS.REPURPOSE_DATE)}
              value={formatToDate(
                getValueByLabel(
                  NESTED_DETAILS.REPURPOSE_DATE,
                  additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                )
              )}
            />
            <Detail
              label={getLabel(NESTED_DETAILS.TECHNICAL_REVIEW_DATE)}
              value={formatToDate(
                getValueByLabel(
                  NESTED_DETAILS.TECHNICAL_REVIEW_DATE,
                  additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                )
              )}
            />
            <Detail
              label={getLabel(NESTED_DETAILS.REMANUFACTURING_TYPE)}
              value={getValueByLabel(
                NESTED_DETAILS.REMANUFACTURING_TYPE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
          </CardRow>
        </Col>
        <Col md={24} lg={14}>
          <RelatedCertificates
            additionalDetails={additionalDetails}
            relatedFields={['batteryPackSerialNumber']}
          />
        </Col>
      </Row>

      <SectionTitle>{getLabel('specification')}</SectionTitle>
      <SubTitle>{getLabel('batteryPack')}</SubTitle>
      <CompositionRow<NESTED_DETAILS>
        composition={additionalDetails?.[ADDITIONAL_DETAILS.DETAILS]}
        labels={[
          NESTED_DETAILS.CELLS_SETUP,
          NESTED_DETAILS.TOTAL_CAPACITY,
          NESTED_DETAILS.USABLE_CAPACITY,
          NESTED_DETAILS.WEIGHT,
          NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY_CLEAN,
        ]}
      />

      <SectionTitle>{getLabel('repurposeCompany')}</SectionTitle>
      <Map
        pins={pins}
        card={
          <BasicCard>
            <SectionTitle noMargin dividerMargin="1rem">
              {getLabel(PRODUCER_DETAILS.COMPANY)}
            </SectionTitle>
            <Detail
              label={getLabel('repurposeCompany')}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel('repurposeAddress')}
              value={getValueByLabel(
                PRODUCER_DETAILS.FACTORY_LOCATION,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel('geoLocation')}
              value={getValueByLabel(
                PRODUCER_DETAILS.COORDINATES,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
          </BasicCard>
        }
      />
    </Template>
  );
};

export default BatteryRepurpose;
